import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

export default function PrivacyPolicy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const updated = "July 2, 2026";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-emerald-600 hover:text-emerald-700 mb-6">
          <ArrowLeft className="w-4 h-4" />Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="p-2.5 rounded-xl bg-emerald-100 dark:bg-emerald-900/40">
            <Shield className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Privacy Policy</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Last updated: {updated}</p>
          </div>
        </div>

        <div className="prose prose-sm sm:prose dark:prose-invert max-w-none space-y-6">
          <section>
            <p>
              Grow Sport Psychology & Mental Performance Improvement (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) is committed to protecting 
              your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you use our web application and related services (the &ldquo;Platform&rdquo;).
            </p>
            <p>By using the Platform, you consent to the practices described in this Privacy Policy.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">1. Information We Collect</h2>
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">a. Information You Provide</h3>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, and password when you register.</li>
              <li><strong>Profile Information:</strong> Sport, position, team name, competition level, and other athletic profile details you choose to provide.</li>
              <li><strong>Assessment Data:</strong> Responses to psychological assessments (APSQ, ACSI-28, skill assessments) used to personalize your training plans.</li>
              <li><strong>Wellness Check-In Data:</strong> Daily self-reported metrics including mood, energy, confidence, focus, stress level, and sleep quality.</li>
              <li><strong>Journal Entries:</strong> Free-written journal content and AI-prompted reflections.</li>
              <li><strong>AI Coach Conversations:</strong> Messages exchanged with our AI-powered coaching feature.</li>
              <li><strong>Training Activity:</strong> Exercise completion records, training plan progress, routine completions, and session notes.</li>
              <li><strong>Goal Data:</strong> Goals you create, including descriptions, timelines, and progress updates.</li>
            </ul>

            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">b. Information Collected Automatically</h3>
            <ul>
              <li><strong>Usage Data:</strong> Pages visited, features used, and interaction patterns to improve the Platform.</li>
              <li><strong>Device Information:</strong> Browser type, operating system, and device type for compatibility purposes.</li>
              <li><strong>Cookies:</strong> We use essential cookies and local storage to maintain your session and preferences (e.g., dark mode, authentication tokens).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">2. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, maintain, and improve the Platform and its features.</li>
              <li>Generate personalized AI-driven training plans, mental warmups, pre-game routines, and coaching recommendations.</li>
              <li>Track your wellness trends, training streaks, and performance metrics over time.</li>
              <li>Send you relevant notifications, reminders (e.g., check-in reminders, assessment reminders), and updates about your training progress.</li>
              <li>Respond to your inquiries and provide customer support.</li>
              <li>Analyze usage patterns to enhance the user experience and develop new features.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">3. AI-Generated Content</h2>
            <p>
              Our Platform uses artificial intelligence (OpenAI GPT-4o) to generate personalized training plans, 
              coaching advice, journal prompts, workout recommendations, and audio content. Please be aware that:
            </p>
            <ul>
              <li>AI-generated content is intended for informational and educational purposes only and does not constitute professional medical, psychological, or therapeutic advice.</li>
              <li>Your input data (assessment scores, wellness check-ins, goals, sport/position) is sent to our AI service provider to generate personalized content. This data is processed in accordance with OpenAI&apos;s usage policies.</li>
              <li>We do not use your personal data to train AI models. Your data is used only to generate responses specific to your requests.</li>
              <li>AI-generated voice audio (text-to-speech) is cached on our servers to improve performance and is associated with your account.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">4. Data Sharing and Disclosure</h2>
            <p>We do not sell, rent, or trade your personal information. We may share your data only in the following circumstances:</p>
            <ul>
              <li><strong>With Your Consent:</strong> When you explicitly share training plans with other users via the Platform&apos;s sharing feature.</li>
              <li><strong>Service Providers:</strong> With trusted third-party services that help us operate the Platform (e.g., AI processing, email delivery, cloud hosting). These providers are contractually obligated to protect your data.</li>
              <li><strong>Team Features:</strong> If you are part of a team, your coach or team administrator may have access to aggregated performance data as part of the team management features. Individual journal entries and private notes are never shared.</li>
              <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process, or to protect the rights, safety, or property of our users or the public.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">5. Data Security</h2>
            <p>We take the security of your data seriously and implement appropriate measures including:</p>
            <ul>
              <li>Encrypted passwords using industry-standard bcrypt hashing.</li>
              <li>JWT-based authentication with secure token handling.</li>
              <li>Rate limiting on authentication endpoints to prevent brute-force attacks.</li>
              <li>HTTPS encryption for all data in transit.</li>
              <li>Access controls ensuring users can only access their own data.</li>
            </ul>
            <p>
              While we strive to protect your information, no method of electronic transmission or storage is 100% 
              secure. We cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">6. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as needed to provide 
              you with our services. If you wish to delete your account and associated data, please contact us at 
              the email address below. We will process your request within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">7. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul>
              <li>Access the personal data we hold about you.</li>
              <li>Request correction of inaccurate or incomplete data.</li>
              <li>Request deletion of your personal data.</li>
              <li>Object to or restrict processing of your data.</li>
              <li>Export your data in a portable format.</li>
            </ul>
            <p>To exercise any of these rights, please contact us using the information below.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">8. Children&apos;s Privacy</h2>
            <p>
              Our Platform is intended for athletes of all ages; however, users under 13 years of age must have 
              parental or guardian consent to use the Platform. If we learn that we have collected personal 
              information from a child under 13 without parental consent, we will take steps to delete that 
              information promptly.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by 
              posting the updated policy on the Platform with a revised &ldquo;Last Updated&rdquo; date. Your continued use 
              of the Platform after changes are posted constitutes your acceptance of the revised policy.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">10. Contact Us</h2>
            <p>If you have questions or concerns about this Privacy Policy or our data practices, please contact us at:</p>
            <p>
              <strong>Grow Sport Psychology & Mental Performance Improvement</strong><br />
              Email: <a href="mailto:sports@growwellnessgroup.com" className="text-emerald-600 hover:text-emerald-700">sports@growwellnessgroup.com</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
